// src/components/intentions/AvailabilitySection.tsx
import React from 'react';
import { Info, MessageCircle } from 'lucide-react';
import { AvailabilityData, FtePreference, DayOffPreference } from '../../types/intentionPeriods';

interface AvailabilitySectionProps {
  availability: AvailabilityData;
  onChange: (availability: AvailabilityData) => void;
  disabled?: boolean;
}

interface FteOption {
  value: FtePreference;
  label: string;
  description?: string;
  numericFte: number;
}

const FTE_OPTIONS: FteOption[] = [
  { value: '1',       label: 'Full time (1.0)',             description: 'No days off',           numericFte: 1.0 },
  // 0.9 was missing, so anyone wanting it had to pick "discuss" instead — which
  // is how 19 staff ended up in the discuss queue and why Claudio Parissi could
  // not record the 0.9 he is already on. No day-off sub-question: 0.9 is a half
  // day rather than a whole one, and the existing options only model whole days.
  { value: '0.9',     label: '0.9 FTE',                     description: 'Half a day off per week', numericFte: 0.9 },
  { value: '0.8',     label: '0.8 FTE',                     description: '1 day off per week',    numericFte: 0.8 },
  { value: '0.75',    label: '0.75 FTE',                    description: '1 day off per week',    numericFte: 0.75 },
  { value: '0.6',     label: '0.6 FTE',                     description: '2 days off per week',   numericFte: 0.6 },
  { value: '0.5',     label: '0.5 FTE',                     description: '2 days off per week',   numericFte: 0.5 },
  { value: '0.4',     label: '0.4 FTE',                     numericFte: 0.4 },
  { value: 'discuss', label: "I'd like to discuss my options", description: 'A leader will reach out to you', numericFte: 1.0 }
];

interface DayOffOption {
  value: DayOffPreference;
  label: string;
}

const DAY_OFF_OPTIONS_HALF: DayOffOption[] = [
  { value: 'wed-thu',  label: 'Wednesday & Thursday off' },
  { value: 'mon-fri',  label: 'Monday & Friday off' },
  { value: 'none',     label: 'No preference' },
  { value: 'discuss',  label: "I'd like to discuss my options" }
];

const DAY_OFF_OPTIONS_THREE_QUARTER: DayOffOption[] = [
  { value: 'wed-only', label: 'Wednesday off' },
  { value: 'fri-only', label: 'Friday off' },
  { value: 'none',     label: 'No preference' },
  { value: 'discuss',  label: "I'd like to discuss my options" }
];

const AvailabilitySection: React.FC<AvailabilitySectionProps> = ({
  availability,
  onChange,
  disabled = false
}) => {
  const handleFteChange = (pref: FtePreference) => {
    const opt = FTE_OPTIONS.find(o => o.value === pref);
    // Clear dayOffPreference unless moving to a load that offers a day choice
    // (0.5/0.6 = two days off, 0.75/0.8 = one day off). 1.0, 0.4 and discuss don't apply.
    const nextDayOff: DayOffPreference | undefined =
      pref === '0.5' || pref === '0.6' || pref === '0.75' || pref === '0.8'
        ? availability.dayOffPreference
        : undefined;
    onChange({
      ...availability,
      ftePreference: pref,
      fte: opt?.numericFte ?? 1.0,
      dayOffPreference: nextDayOff
    });
  };

  const handleDayOffChange = (pref: DayOffPreference) => {
    onChange({
      ...availability,
      dayOffPreference: pref
    });
  };

  const handleLeavePlansChange = (value: string) => {
    onChange({
      ...availability,
      leavePlans: value
    });
  };

  const ftePref = availability.ftePreference;
  // 0.5 and 0.6 share the "two days off" set; 0.75 and 0.8 share the "one day off" set.
  const isTwoDayOff = ftePref === '0.5' || ftePref === '0.6';
  const isOneDayOff = ftePref === '0.75' || ftePref === '0.8';
  const dayOffOptions =
    isTwoDayOff ? DAY_OFF_OPTIONS_HALF :
    isOneDayOff ? DAY_OFF_OPTIONS_THREE_QUARTER :
    null;

  const showDiscussBanner = ftePref === 'discuss' || availability.dayOffPreference === 'discuss';

  return (
    <div className="bg-white rounded-lg border border-ink-200 p-6">
      <h3 className="text-lg font-semibold text-ink-900 mb-4">Availability</h3>

      {/* FTE / Load Percentage */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-ink-700 mb-2">
          Preferred Load <span className="text-danger-fg">*</span>
        </label>
        <p className="text-xs text-ink-500 mb-3">
          Select one. If none of these match what you'd like, choose "I'd like to discuss my options" and someone will reach out.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FTE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                ftePref === opt.value
                  ? 'border-navy-600 bg-navy-50'
                  : 'border-ink-300 bg-white hover:bg-ink-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="radio"
                name="ftePreference"
                value={opt.value}
                checked={ftePref === opt.value}
                onChange={() => handleFteChange(opt.value)}
                disabled={disabled}
                className="sr-only"
              />
              <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                ftePref === opt.value ? 'border-navy-600' : 'border-ink-400'
              }`}>
                {ftePref === opt.value && (
                  <div className="w-2 h-2 bg-navy-600 rounded-full" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-ink-900">{opt.label}</div>
                {opt.description && (
                  <div className="text-xs text-ink-500 mt-0.5">{opt.description}</div>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Day Off Preference — only shown for 0.5 and 0.75 FTE */}
      {dayOffOptions && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-ink-700 mb-2">
            Preferred day{isTwoDayOff ? 's' : ''} off <span className="text-danger-fg">*</span>
          </label>
          <p className="text-xs text-ink-500 mb-3">
            {isTwoDayOff
              ? 'Which two days would you prefer to have off each week?'
              : 'Which day would you prefer to have off each week?'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {dayOffOptions.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  availability.dayOffPreference === opt.value
                    ? 'border-navy-600 bg-navy-50'
                    : 'border-ink-300 bg-white hover:bg-ink-50'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  name="dayOffPreference"
                  value={opt.value}
                  checked={availability.dayOffPreference === opt.value}
                  onChange={() => handleDayOffChange(opt.value)}
                  disabled={disabled}
                  className="sr-only"
                />
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  availability.dayOffPreference === opt.value ? 'border-navy-600' : 'border-ink-400'
                }`}>
                  {availability.dayOffPreference === opt.value && (
                    <div className="w-2 h-2 bg-navy-600 rounded-full" />
                  )}
                </div>
                <span className="font-medium text-ink-900">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* "Discuss" info banner */}
      {showDiscussBanner && (
        <div className="mb-6 p-4 bg-warn-bg border border-warn-border rounded-lg flex gap-3">
          <MessageCircle className="w-5 h-5 text-warn-fg flex-shrink-0 mt-0.5" />
          <div className="text-sm text-warn-fg">
            <p className="font-medium mb-1">Your preference has been flagged for a conversation.</p>
            <p>
              Someone will be in touch to discuss options with you. You can still submit the rest of the form — this just means we'll follow up with you individually on your load/day preference.
            </p>
          </div>
        </div>
      )}

      {/* Leave / Sabbatical Plans — kept as free-text supplement */}
      <div>
        <label className="block text-sm font-medium text-ink-700 mb-2">
          Leave / Sabbatical Plans
        </label>
        <p className="text-xs text-ink-500 mb-2 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 text-ink-400 flex-shrink-0 mt-0.5" />
          <span>Optional. Use this if you already noted leave plans above and want to add any further context.</span>
        </p>
        <textarea
          value={availability.leavePlans || ''}
          onChange={(e) => handleLeavePlansChange(e.target.value)}
          disabled={disabled}
          rows={3}
          className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500 text-sm"
          placeholder="Any additional context about leave, sabbatical, or extended absence..."
        />
      </div>
    </div>
  );
};

export default AvailabilitySection;
